import React from "react";
import { GetStaticPaths, GetStaticProps, NextPage } from "next";
import Head from "next/head";
import Link from "next/link";
import PageLayoutV1 from "@app/hocs/PageLayoutV1";
import * as CmsHttp from "@app/http/cms";
import { Expert } from "@models/Expert";
import SimpleExpertDetailCard from "@components/experts/SimpleExpertDetailCard";
import { META_DESCRIPTION, META_TITLE, META_URL, REVALIDATE_SECONDS } from "@app/utilities/constants";
import { sliceMetaDescription } from "@app/utilities/helpers";
import { CmsMeta } from "@app/http/cms/components/CmsMeta";

interface ITeamDetailPage {
  expert: Expert;
}

const TeamDetailPage: NextPage<ITeamDetailPage> = (props: ITeamDetailPage) => {
  const { expert } = props,
    expertFullName = [expert.firstName, expert.lastName].join(" "),
    expertCredentials = expert.credentials.join(", "),
    expertMetaDescription = sliceMetaDescription(expert.description.text);

  /**
   * Meta contents
   */
  let metaTitle = META_TITLE || "",
    metaDescription = META_DESCRIPTION || "",
    metaUrl = META_URL || "",
    metaOgImg = META_URL + `/logo.png`,
    metaTwitterImg = META_URL + `/logo.png`;

  if (expert) {
    metaTitle = `${expertFullName} | Heritage Counseling`;
    metaDescription = expertMetaDescription || metaDescription;
    metaUrl = `${META_URL}/teams/${expert.slug}`;
    metaOgImg = expert.image?.url || metaOgImg;
    metaTwitterImg = expert.image?.url || metaTwitterImg;
  }

  // Override metas using pageMeta
  const pageMeta = (expert?.meta || {}) as CmsMeta;
  if (Object.keys(pageMeta).length > 0) {
    metaTitle = pageMeta?.title || metaTitle;
    metaDescription = pageMeta?.description || metaDescription;
    metaOgImg = pageMeta?.ogImage?.url || metaOgImg;
    metaTwitterImg = pageMeta?.twitterImage?.url || metaTwitterImg;
  }

  return (
    <PageLayoutV1 title={expertFullName} tagline={expertCredentials}>
      <Head>
        <title>{metaTitle}</title>
        <meta key="title" name="title" content={metaTitle} />
        <meta key="description" name="description" content={metaDescription} />

        <meta key="og:title" property="og:title" content={metaTitle} />
        <meta key="og:description" property="og:description" content={metaDescription} />
        <meta key="og:url" property="og:url" content={metaUrl} />
        <meta key="og:image" property="og:image" content={metaOgImg} />

        <meta key="twitter:url" name="twitter:url" content={metaUrl} />
        <meta key="twitter:title" name="twitter:title" content={metaTitle} />
        <meta key="twitter:description" name="twitter:description" content={metaDescription} />
        <meta key="twitter:image" name="twitter:image" content={metaTwitterImg} />

        <meta key="prop:title" itemProp="title" content={metaTitle} />
        <meta key="prop:description" itemProp="description" content={metaDescription} />

        <link rel="canonical" href={metaUrl} />
        {expert?.schema && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(expert.schema),
            }}
          ></script>
        )}
      </Head>
      <div className="team-detail-page" data-testid="team-detail-page">
        <nav className="mb-5 mb-lg-10" role="navigation">
          <Link href="/teams">
            <a className="link-back">
              <i className="fa fa-angle-left"></i>
              Back to Teams
            </a>
          </Link>
        </nav>
        {expert ? (
          <div className="wrapper">
            <SimpleExpertDetailCard expert={expert} />
            <section className="bio" dangerouslySetInnerHTML={{ __html: expert.bio.html }}></section>
            {!!expert.area_of_expertises?.length && (
              <section className="specialties">
                <label className="title">Specialties</label>
                <div className="auto-grid service-links">
                  {expert.area_of_expertises.map((service, idx) => (
                    <Link href={`/services/${service.typeSlug}/${service.slug}`} key={idx}>
                      <a className="link">{service.name}</a>
                    </Link>
                  ))}
                </div>
              </section>
            )}
            {!!expert.issues_treated?.length && (
              <section className="issues">
                <label className="title">Issues Treated</label>
                <div className="auto-grid service-links">
                  {expert.issues_treated.map((service, idx) => (
                    <Link href={`/services/${service.typeSlug}/${service.slug}`} key={idx}>
                      <a className="link">{service.name}</a>
                    </Link>
                  ))}
                </div>
              </section>
            )}
            {!!expert.blogs?.length && (
              <section className="blogs">
                <label className="title">Blogs</label>
                <div className="">
                  {expert.blogs.map((blog, idx) => {
                    return (
                      <Link key={idx} href={`/blogs/${blog.slug}`}>
                        <a className="blog-link">
                          <img className="img-blog" src={blog.featuredImage?.src || ""} alt={blog.title} height={70} width={70} />
                          <div className="blog-body">
                            <h5 className="blog-title">{blog.title}</h5>
                          </div>
                        </a>
                      </Link>
                    );
                  })}
                </div>
              </section>
            )}
          </div>
        ) : (
          <p className="text-center">No experts found</p>
        )}
        <nav className="mt-5 mt-lg-10" role="navigation">
          <Link href="/teams">
            <a className="link-back">
              <i className="fa fa-angle-left"></i>
              Back to Teams
            </a>
          </Link>
        </nav>
      </div>
    </PageLayoutV1>
  );
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const slug = (params && Array.isArray(params?.slug) ? params.slug[0] : params?.slug) as string,
    ssrExpert = await CmsHttp.getExpertBySlug(slug);

  return {
    props: {
      expert: ssrExpert || null,
    } as ITeamDetailPage,
    notFound: !ssrExpert?.slug,
    revalidate: REVALIDATE_SECONDS, // In seconds
  };
};

export const getStaticPaths: GetStaticPaths = async () => {
  const models = await CmsHttp.getExperts(),
    slugs = models.map((model) => model?.slug || "").filter(Boolean);

  return {
    paths: slugs.map((slug) => ({
      params: {
        slug,
      },
    })),
    fallback: "blocking",
  };
};

export default TeamDetailPage;
